import { useState, useEffect, lazy, Suspense, useCallback, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { Target, RefreshCw, Download, Crown, Search, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import type { Player, GameData, HitData, SavedHighscore } from '@/types/game';
import { formatGameNumber, parseGameDate, isRateLimitError } from '@/utils/game-utils';
import { ShareButtons } from '@/components/highscore/ShareButtons';
import { PlayerCard } from '@/components/highscore/PlayerCard';
import { TeamRankings } from '@/components/highscore/TeamRankings';
import { HitMatrix } from '@/components/highscore/HitMatrix';

const TournamentMode = lazy(() => import('./TournamentMode').then(m => ({ default: m.TournamentMode })));

export const HighscoreBoard = () => {
  const [gameData, setGameData] = useState<GameData | null>(null);
  const [hitData, setHitData] = useState<HitData[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [savedHighscores, setSavedHighscores] = useState<SavedHighscore[]>([]);
  const [selectedHighscoreId, setSelectedHighscoreId] = useState<string>('auto');
  const [hasManualSelection, setHasManualSelection] = useState(false);
  const [showTournamentMode, setShowTournamentMode] = useState(false);
  const [manualGameNumber, setManualGameNumber] = useState('');
  const [loadingManualGame, setLoadingManualGame] = useState(false);
  const [showCTA, setShowCTA] = useState(false);

  const gameDataRef = useRef<GameData | null>(null);
  gameDataRef.current = gameData;

  const { toast } = useToast();

  // Show CTA button only after scrolling down
  useEffect(() => {
    const handleScroll = () => setShowCTA(window.scrollY > 200);
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // --- Data fetching helpers ---

  const fetchViaProxy = useCallback(async (url: string): Promise<string> => {
    const { data, error } = await supabase.functions.invoke('fetch-game-data', {
      body: { url }
    });
    if (error) throw new Error(`Failed to fetch: ${error.message}`);
    if (!data?.data) throw new Error('No data returned');
    return data.data;
  }, []);

  const parseJsonResponse = (raw: string): GameData => {
    try {
      return JSON.parse(raw);
    } catch {
      const start = raw.indexOf('{');
      const end = raw.lastIndexOf('}');
      if (start !== -1 && end !== -1 && end > start) {
        return JSON.parse(raw.slice(start, end + 1));
      }
      throw new Error('Antwort enthält kein parsebares JSON');
    }
  };

  const parseGameFile = useCallback((content: string, playersParam?: Player[]): HitData[] => {
    try {
      const sections = content.split('#').filter(s => s.trim());
      const playerMap = new Map<string, string>();
      const playerUrlMap = new Map<string, string>();
      const hitMatrix: { [shooter: string]: { [target: string]: number } } = {};
      const packOrderWestes: string[] = [];

      sections.forEach(section => {
        const trimmed = section.trim();
        if (trimmed.startsWith('PACK{')) {
          const packData = trimmed.match(/PACK\{([^}]+)\}/);
          if (packData) {
            const parts = packData[1].split(',');
            if (parts.length >= 2) {
              const weste = parts[0].trim();
              playerMap.set(weste, parts[1].trim());
              packOrderWestes.push(weste);
            }
          }
        }
        if (trimmed.startsWith('PACKX{')) {
          const packxData = trimmed.match(/PACKX\{([^}]+)\}/);
          if (packxData) {
            const parts = packxData[1].split(',');
            if (parts.length >= 7) {
              const weste = parts[0].trim();
              const url = parts[6].trim();
              if (url?.startsWith('http')) playerUrlMap.set(weste, url);
            }
          }
        }
      });

      sections.forEach(section => {
        const trimmed = section.trim();
        if (trimmed.includes('HITS{')) {
          const hitsMatches = trimmed.match(/HITS\{([^}]+)\}/g);
          hitsMatches?.forEach(hit => {
            const data = hit.match(/HITS\{([^}]+)\}/);
            if (data) {
              const parts = data[1].split(',').map(s => s.trim());
              if (parts.length >= 2) {
                const shooter = parts[0];
                if (!hitMatrix[shooter]) hitMatrix[shooter] = {};
                for (let i = 1; i < parts.length; i++) {
                  const val = parseInt(parts[i], 10) || 0;
                  const targetIndex = i - 1;
                  if (targetIndex < packOrderWestes.length && val > 0) {
                    hitMatrix[shooter][packOrderWestes[targetIndex]] = val;
                  }
                }
              }
            }
          });
        }
      });

      const playersList = playersParam ?? gameDataRef.current?.players ?? [];
      if (playersList.length > 0) {
        return playersList.map(p => {
          const url = playerUrlMap.get(p.weste);
          if (url && !p.playerurl) p.playerurl = url;
          return { player: p.name, targets: hitMatrix[p.weste] || {} };
        });
      }

      return packOrderWestes.map(weste => ({
        player: playerMap.get(weste) || `Weste ${weste}`,
        targets: hitMatrix[weste] || {}
      }));
    } catch (error) {
      console.error('Error parsing game file:', error);
      return [];
    }
  }, []);

  const fetchDetailData = useCallback(async (filename: string, players?: Player[]) => {
    try {
      const gameUrl = `https://lasertag-arena-siegen.de/highscores/games/${filename}?_=${Date.now()}`;
      const raw = await fetchViaProxy(gameUrl);
      const processedHitData = parseGameFile(raw, players);
      setHitData(processedHitData.length > 0 ? processedHitData :
        (players?.map(p => ({ player: p.name, targets: {} })) || null));
    } catch {
      setHitData(null);
    }
  }, [fetchViaProxy, parseGameFile]);

  const fetchGameByNumber = useCallback(async (gameNumber: string): Promise<GameData> => {
    const paddedNumber = gameNumber.padStart(4, '0');
    const apiUrl = `https://lasertag-arena-siegen.de/highscores/assets/php/import.php?game=${paddedNumber}&_=${Date.now()}`;
    const raw = await fetchViaProxy(apiUrl);
    const data = parseJsonResponse(raw);
    if (!data?.players || !data?.teams) throw new Error('Spiel nicht gefunden oder ungültiges Format');
    return data;
  }, [fetchViaProxy]);

  const saveHighscore = useCallback(async (data: GameData) => {
    try {
      const response = await supabase.functions.invoke('save-highscore', {
        body: { game_data: data, game_id: data.game },
      });

      const { data: result, error } = response;

      if (error) {
        const errorStr = JSON.stringify(error) + (error.message || '') + (error.context?.body || '');
        if (isRateLimitError(errorStr)) return;
        console.error('Error calling save-highscore:', error);
        return;
      }

      if (result?.error) {
        if (isRateLimitError(result.error)) return;
        console.error('Failed to save highscore:', result.error);
        return;
      }

      if (result?.success) {
        toast({ title: "Highscore gespeichert!", description: `Spiel #${data.game} wurde erfolgreich gespeichert.` });
        loadSavedHighscores(false);
      }
    } catch (error: any) {
      const errorStr = String(error) + (error?.message || '') + JSON.stringify(error || {});
      if (isRateLimitError(errorStr)) return;
      console.error('Error in saveHighscore:', error);
    }
  }, [toast]);

  // --- Unified highscore loading ---

  const deduplicateAndSort = (data: any[]): SavedHighscore[] => {
    const gameMap = new Map<string, any>();
    (data || []).forEach((row: any) => {
      if (!gameMap.has(row.game_id)) gameMap.set(row.game_id, row);
    });

    return Array.from(gameMap.values())
      .sort((a, b) => {
        const timeA = parseGameDate(a.game_data).getTime();
        const timeB = parseGameDate(b.game_data).getTime();
        if (timeA !== timeB) return timeB - timeA;
        return parseInt(b.game_id) - parseInt(a.game_id);
      })
      .slice(0, 10) as SavedHighscore[];
  };

  const loadSavedHighscores = useCallback(async (fetchLiveData: boolean = false) => {
    try {
      if (fetchLiveData) await fetchData();

      const { data, error } = await supabase
        .from('highscores')
        .select('*')
        .order('timestamp', { ascending: false })
        .limit(50);

      if (error) { console.error('Error loading highscores:', error); return; }

      const uniqueList = deduplicateAndSort(data || []);
      setSavedHighscores(uniqueList);

      if (uniqueList.length > 0 && !hasManualSelection) {
        setSelectedHighscoreId(uniqueList[0].id);

        if (fetchLiveData) {
          const highestGameNumber = parseInt(uniqueList[0].game_id);
          if (highestGameNumber > 0) {
            setTimeout(() => backfillMissingGames(highestGameNumber), 5000);
          }
        }
      }
    } catch (error) {
      console.error('Error in loadSavedHighscores:', error);
    }
  }, [hasManualSelection]);

  const fetchData = useCallback(async () => {
    try {
      const apiUrl = `https://lasertag-arena-siegen.de/highscores/assets/php/import.php?_=${Date.now()}`;
      const raw = await fetchViaProxy(apiUrl);
      const data = parseJsonResponse(raw);

      if (!data?.players || !data?.teams) throw new Error('Unerwartetes Datenformat');

      const isNewGame = !gameDataRef.current || gameDataRef.current.game !== data.game;

      setGameData(data);
      setLoading(false);

      if (isNewGame) await saveHighscore(data);
      if (data.file) fetchDetailData(data.file, data.players);
    } catch (error) {
      console.error('Failed to fetch game data:', error);

      try {
        const { data, error } = await supabase
          .from('highscores')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!error && data) {
          const gd = data.game_data as unknown as GameData;
          setGameData(gd);
          if (gd.file) fetchDetailData(gd.file, gd.players);
        }
      } catch (dbError) {
        console.error('Failed to load fallback data:', dbError);
      }

      setLoading(false);
    }
  }, [fetchViaProxy, saveHighscore, fetchDetailData]);

  const backfillMissingGames = useCallback(async (latestGameNumber: number) => {
    try {
      const { data: existingGames, error } = await supabase
        .from('highscores')
        .select('game_id')
        .order('game_id', { ascending: false })
        .limit(50);

      if (error) return;

      const existingGameIds = new Set((existingGames || []).map(g => g.game_id));
      const gamesToFetch: number[] = [];
      for (let i = 1; i <= 10; i++) {
        const gameNum = latestGameNumber - i;
        if (gameNum > 0 && !existingGameIds.has(gameNum.toString().padStart(4, '0'))) {
          gamesToFetch.push(gameNum);
        }
      }

      if (gamesToFetch.length === 0) return;

      for (const gameNum of gamesToFetch) {
        try {
          const data = await fetchGameByNumber(gameNum.toString());
          if (data?.players && data?.teams) {
            await supabase.functions.invoke('save-highscore', {
              body: { game_data: data, game_id: data.game },
            });
          }
        } catch {
          // Continue with next game
        }
      }

      // Refresh list after backfill
      const { data: refreshed } = await supabase
        .from('highscores')
        .select('*')
        .order('timestamp', { ascending: false })
        .limit(50);
      if (refreshed) setSavedHighscores(deduplicateAndSort(refreshed));
    } catch {
      // Backfill is best-effort
    }
  }, [fetchGameByNumber]);

  const loadHighscoreById = useCallback(async (id: string) => {
    if (id === 'live') { fetchData(); return; }
    if (id === 'auto') return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('highscores')
        .select('*')
        .eq('id', id)
        .single();

      if (error) { setLoading(false); return; }

      const gd = data.game_data as unknown as GameData;
      setGameData(gd);
      setLoading(false);
      setHitData(null);

      if (gd.file) {
        fetchDetailData(gd.file, gd.players).catch(() => {
          setHitData(gd.players.map(p => ({ player: p.name, targets: {} })));
        });
      } else {
        setHitData(gd.players.map(p => ({ player: p.name, targets: {} })));
      }
    } catch {
      setLoading(false);
    }
  }, [fetchData, fetchDetailData]);

  const handleManualGameFetch = async () => {
    const gameNum = manualGameNumber.trim();
    if (!gameNum || !/^\d+$/.test(gameNum)) {
      if (gameNum) toast({ title: "Ungültige Spielnummer", description: "Bitte geben Sie nur Zahlen ein.", variant: "destructive" });
      return;
    }

    setLoadingManualGame(true);
    try {
      const data = await fetchGameByNumber(gameNum);
      setGameData(data);
      await saveHighscore(data);
      if (data.file) fetchDetailData(data.file, data.players);
      toast({ title: "Spiel geladen!", description: `Spiel #${data.game} wurde erfolgreich abgerufen und gespeichert.` });
      setManualGameNumber('');
    } catch (error) {
      toast({
        title: "Fehler beim Laden",
        description: error instanceof Error ? error.message : "Das Spiel konnte nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setLoadingManualGame(false);
    }
  };

  const fetchBase64Image = async (url: string): Promise<string | null> => {
    try {
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) return null;
      const blob = await res.blob();
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch { return null; }
  };

  const downloadPDF = async () => {
    if (!gameData) return;
    try {
      toast({ title: "PDF wird erstellt...", description: "Bitte warten Sie einen Moment." });
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      const gutscheinBase64 = await fetchBase64Image('/gutschein.png');

      const response = await fetch(`${supabaseUrl}/functions/v1/generate-highscore-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${supabaseAnonKey}` },
        body: JSON.stringify({ game_data: gameData, hit_data: hitData, gutschein_image_base64: gutscheinBase64 || undefined }),
      });

      if (!response.ok) throw new Error('PDF generation failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `highscore-${gameData.game}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({ title: "PDF heruntergeladen!", description: "Das Highscore-PDF wurde erfolgreich heruntergeladen." });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast({ title: "Fehler", description: "PDF konnte nicht erstellt werden.", variant: "destructive" });
    }
  };

  // --- Effects ---

  useEffect(() => {
    loadSavedHighscores(true);
    const interval = setInterval(() => {
      if (!hasManualSelection) loadSavedHighscores(false);
    }, 30000);
    return () => clearInterval(interval);
  }, [hasManualSelection]);

  useEffect(() => {
    if (selectedHighscoreId !== 'auto') loadHighscoreById(selectedHighscoreId);
  }, [selectedHighscoreId]);

  // --- Render ---

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <RefreshCw className="w-8 h-8 animate-spin text-neon-cyan mx-auto" />
          <p className="text-xl text-neon-cyan">Lade Highscores...</p>
        </div>
      </div>
    );
  }

  if (!gameData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-xl text-muted-foreground">Keine Daten verfügbar</p>
      </div>
    );
  }

  if (showTournamentMode) {
    return (
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      }>
        <TournamentMode onClose={() => setShowTournamentMode(false)} />
      </Suspense>
    );
  }

  const sortedPlayers = [...gameData.players].sort((a, b) => parseInt(b.score) - parseInt(a.score));

  return (
    <>
      <div className="min-h-screen p-2 sm:p-4 space-y-4 sm:space-y-8 pb-20 sm:pb-24">
        {/* Highscore Selector */}
        <Card className="p-3 sm:p-4 border-2 border-primary/20 bg-card/50 backdrop-blur-sm">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex-1">
              <h3 className="text-base sm:text-lg font-semibold">Highscore Auswahl</h3>
            </div>

            <ShareButtons gameNumber={gameData.game} />

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
              <Select
                value={selectedHighscoreId}
                onValueChange={(value) => { setSelectedHighscoreId(value); setHasManualSelection(true); }}
              >
                <SelectTrigger className="w-full sm:w-[250px] bg-background" aria-label="Highscore-Spiel auswählen">
                  <SelectValue placeholder="Wähle einen Highscore" />
                </SelectTrigger>
                <SelectContent className="bg-background z-50">
                  <SelectItem value="live">🔴 Live - Aktuelles Spiel</SelectItem>
                  {savedHighscores.map((highscore) => (
                    <SelectItem key={highscore.id} value={highscore.id}>
                      Spiel #{formatGameNumber(highscore.game_data.game)} - {highscore.game_data.timestamp}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={downloadPDF} variant="outline" size="default" className="flex items-center gap-2 border-primary/20 hover:bg-primary/10 w-full sm:w-auto">
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Als PDF</span>
                <span className="sm:hidden">PDF</span>
              </Button>
              <Button onClick={() => setShowTournamentMode(true)} variant="outline" size="default" className="flex items-center gap-2 border-neon-yellow/30 hover:bg-team-yellow text-neon-yellow w-full sm:w-auto">
                <Crown className="w-4 h-4" />
                <span className="hidden sm:inline">Turnier-Modus</span>
                <span className="sm:hidden">Turnier</span>
              </Button>
            </div>
          </div>

          {/* Manual Game Fetch */}
          <div className="mt-4 pt-4 border-t border-border/50">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
              <span className="text-sm text-muted-foreground whitespace-nowrap">Älteres Spiel abrufen:</span>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <Input
                  type="text"
                  placeholder="z.B. 817"
                  value={manualGameNumber}
                  onChange={(e) => setManualGameNumber(e.target.value.replace(/\D/g, ''))}
                  onKeyDown={(e) => e.key === 'Enter' && handleManualGameFetch()}
                  className="w-24 bg-background"
                  maxLength={4}
                />
                <Button
                  onClick={handleManualGameFetch}
                  disabled={loadingManualGame || !manualGameNumber.trim()}
                  variant="outline"
                  size="default"
                  className="flex items-center gap-2 border-primary/20 hover:bg-primary/10"
                >
                  {loadingManualGame ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  <span>Laden</span>
                </Button>
              </div>
            </div>
          </div>
        </Card>

        <TeamRankings teams={gameData.teams} />

        {/* Player Leaderboard */}
        <Card className="p-3 sm:p-6 border-2 border-primary/20 bg-card/50 backdrop-blur-sm animate-slide-up">
          <h2 className="text-lg sm:text-2xl font-bold mb-2 text-center flex items-center justify-center space-x-2">
            <Target className="w-5 h-5 sm:w-6 sm:h-6 text-neon-cyan" />
            <span>Spieler Rankings</span>
          </h2>
          <p className="text-xs sm:text-sm text-muted-foreground text-center mb-4 sm:mb-6 px-2">
            Mehr Statistiken? Einfach auf deinen Namen klicken und die Online-Scorekarte aktivieren.
          </p>
          <div className="space-y-3">
            {sortedPlayers.map((player, index) => (
              <PlayerCard key={`${player.weste}-${player.name}`} player={player} rank={index + 1} index={index} />
            ))}
          </div>
        </Card>

        <HitMatrix hitData={hitData || []} players={gameData.players} />

        {/* Auto-refresh indicator */}
        <div className="text-center text-sm text-muted-foreground">
          <div className="flex items-center justify-center space-x-2">
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span>Automatische Aktualisierung alle 30 Sekunden</span>
          </div>
        </div>
      </div>

      {/* Sticky CTA Button */}
      <div className={`fixed bottom-0 left-0 right-0 p-2 sm:p-4 bg-gradient-to-t from-background via-background to-transparent pointer-events-none z-50 transition-all duration-300 ${showCTA ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
        <div className="max-w-7xl mx-auto pointer-events-auto">
          <a href="https://onlinebuchung.lasertag-arena-siegen.de" target="_blank" rel="noopener noreferrer" className="block w-full">
            <button className="w-full h-10 sm:h-12 px-4 sm:px-8 bg-gradient-cyber text-white font-bold text-sm sm:text-lg rounded-md shadow-lg hover:shadow-glow-primary transition-all duration-300 hover:scale-[1.01] sm:hover:scale-[1.02] animate-pulse-glow">
              <span className="hidden sm:inline">🎯 Buche jetzt dein nächstes Spiel!</span>
              <span className="sm:hidden">🎯 Jetzt buchen!</span>
            </button>
          </a>
        </div>
      </div>
    </>
  );
};
